/**
 * =====================================================
 * MANUAL NOTIFICATIONS
 * =====================================================
 * Admin page for sending push notifications to selected customers.
 * 
 * Features:
 * - Advanced customer filtering (orders, revenue, date, gender, search)
 * - Select individual or all customers
 * - Compose custom notification with title, body, image
 * - Preview notification before sending
 * - Campaign history with delivery stats
 */

import React, { useState, useEffect, useCallback } from "react";
import { FaBell, FaFilter, FaPaperPlane, FaUsers, FaSearch, FaTimes, FaCheckCircle, FaExclamationTriangle, FaMobileAlt, FaHistory, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { getNotificationFilteredCustomers, sendManualNotification, getNotificationCampaignHistory } from "../../services/ClientService";
import ConfirmModal from "../ConfirmModal";
import NotificationNavigateTargetModal from "./NotificationNavigateTargetModal";

// import moment from "moment";

const ManualNotifications = () => {
    // Customer list state
    const [customers, setCustomers] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [allFilteredIds, setAllFilteredIds] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });

    // Filters
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        searchTerm: '',
        minOrders: '',
        maxOrders: '',
        minRevenue: '',
        maxRevenue: '',
        lastOrderFrom: '',
        lastOrderTo: '',
        gender: '',
        hasOrders: '',
    });

    // Navigation screen options for deep link (simple screens are also available in the modal)
    const [composeForm, setComposeForm] = useState({
        title: '',
        body: '',
        imageUrl: '',
        navigateTo: '',
        navigateParams: null,
        navigateLabel: '',
    });
    const [navigateModalOpen, setNavigateModalOpen] = useState(false);
    const [sending, setSending] = useState(false);

    // Campaign history
    const [activeTab, setActiveTab] = useState('compose'); // 'compose' | 'history'
    const [campaigns, setCampaigns] = useState([]);
    const [campaignsLoading, setCampaignsLoading] = useState(false);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });
    const [confirmModal, setConfirmModal] = useState({ visible: false, message: '', onConfirm: null });
    const showSnackbar = (message, type = 'success') => {
        setSnackbar({ show: true, message, type });
        setTimeout(() => setSnackbar({ show: false, message: '', type: 'success' }), 4000);
    };

    // Fetch customers with filters
    const fetchCustomers = useCallback(async (page = 1) => {
        setCustomersLoading(true);
        try {
            const filterPayload = { page, limit: pagination.limit };
            if (filters.searchTerm) filterPayload.searchTerm = filters.searchTerm;
            if (filters.minOrders) filterPayload.minOrders = Number(filters.minOrders);
            if (filters.maxOrders) filterPayload.maxOrders = Number(filters.maxOrders);
            if (filters.minRevenue) filterPayload.minRevenue = Number(filters.minRevenue);
            if (filters.maxRevenue) filterPayload.maxRevenue = Number(filters.maxRevenue);
            if (filters.lastOrderFrom) filterPayload.lastOrderFrom = filters.lastOrderFrom;
            if (filters.lastOrderTo) filterPayload.lastOrderTo = filters.lastOrderTo;
            if (filters.gender) filterPayload.gender = filters.gender;
            if (filters.hasOrders !== '') filterPayload.hasOrders = filters.hasOrders === 'true';

            const res = await getNotificationFilteredCustomers(filterPayload);
            if (res.success) {
                setCustomers(res.data);
                setPagination(prev => ({ ...prev, page, ...res.pagination }));
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
        } finally {
            setCustomersLoading(false);
        }
    }, [filters, pagination.limit]);

    // Fetch all filtered IDs for "select all"
    const fetchAllFilteredIds = useCallback(async () => {
        try {
            const filterPayload = { selectAll: true };
            if (filters.searchTerm) filterPayload.searchTerm = filters.searchTerm;
            if (filters.minOrders) filterPayload.minOrders = Number(filters.minOrders);
            if (filters.maxOrders) filterPayload.maxOrders = Number(filters.maxOrders);
            if (filters.minRevenue) filterPayload.minRevenue = Number(filters.minRevenue);
            if (filters.maxRevenue) filterPayload.maxRevenue = Number(filters.maxRevenue);
            if (filters.lastOrderFrom) filterPayload.lastOrderFrom = filters.lastOrderFrom;
            if (filters.lastOrderTo) filterPayload.lastOrderTo = filters.lastOrderTo;
            if (filters.gender) filterPayload.gender = filters.gender;
            if (filters.hasOrders !== '') filterPayload.hasOrders = filters.hasOrders === 'true';

            const res = await getNotificationFilteredCustomers(filterPayload);
            if (res.success) {
                setAllFilteredIds(res.data.map(id => id.toString ? id.toString() : id));
            }
        } catch (error) {
            console.error('Error fetching all IDs:', error);
        }
    }, [filters]);

    // Fetch campaign history
    const fetchCampaigns = useCallback(async () => {
        setCampaignsLoading(true);
        try {
            const res = await getNotificationCampaignHistory({ page: 1, limit: 50 });
            if (res.success) setCampaigns(res.data);
        } catch (error) {
            console.error('Error fetching campaigns:', error);
        } finally {
            setCampaignsLoading(false);
        }
    }, []);

    useEffect(() => { fetchCustomers(1); }, [fetchCustomers]);
    useEffect(() => { if (activeTab === 'history') fetchCampaigns(); }, [activeTab, fetchCampaigns]);

    // Selection handlers
    const toggleSelectCustomer = (customerId) => {
        const id = customerId.toString ? customerId.toString() : customerId;
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
        setSelectAll(false);
    };

    const handleSelectAll = async () => {
        if (selectAll) {
            setSelectedIds([]);
            setSelectAll(false);
        } else {
            await fetchAllFilteredIds();
            setSelectAll(true);
        }
    };

    useEffect(() => {
        if (selectAll && allFilteredIds.length > 0) {
            setSelectedIds(allFilteredIds);
        }
    }, [selectAll, allFilteredIds]);

    const effectiveSelectedCount = selectAll ? pagination.total : selectedIds.length;

    // Apply filters
    const handleApplyFilters = () => {
        fetchCustomers(1);
        setSelectedIds([]);
        setSelectAll(false);
    };

    const handleClearFilters = () => {
        setFilters({
            searchTerm: '', minOrders: '', maxOrders: '', minRevenue: '', maxRevenue: '',
            lastOrderFrom: '', lastOrderTo: '', gender: '', hasOrders: '',
        });
        setSelectedIds([]);
        setSelectAll(false);
    };

    // Send notification
    const handleSend = () => {
        if (!composeForm.title.trim() || !composeForm.body.trim()) {
            showSnackbar('Title and body are required', 'error');
            return;
        }
        if (effectiveSelectedCount === 0) {
            showSnackbar('Please select at least one customer', 'error');
            return;
        }
        if (!composeForm.navigateTo) {
            showSnackbar('Please choose where the notification should navigate on tap', 'error');
            setNavigateModalOpen(true);
            return;
        }

        const confirmMessage = `Send notification to ${effectiveSelectedCount} customer${effectiveSelectedCount > 1 ? 's' : ''}?`;
        setConfirmModal({
            visible: true,
            message: confirmMessage,
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, visible: false }));
                setSending(true);
                try {
                    const payload = {
                        title: composeForm.title,
                        body: composeForm.body,
                        imageUrl: composeForm.imageUrl || undefined,
                        userIds: selectedIds,
                    };
                    // Deep link destination is required
                    payload.deepLink = {
                        screen: composeForm.navigateTo,
                        ...(composeForm.navigateParams ? { params: composeForm.navigateParams } : {}),
                    };
                    const res = await sendManualNotification(payload);
                    if (res.success) {
                        const push = res.data?.pushResult || {};
                        const devicesFound = res.data?.devicesFound ?? 0;
                        const pushFailed = (push.failure || 0) > 0 && (push.success || 0) === 0;
                        const noDevices = devicesFound === 0;

                        if (noDevices || pushFailed) {
                            showSnackbar(
                                res.message || 'Notification saved in-app only — push could not be delivered. Customer must open the mobile app while logged in.',
                                'warning'
                            );
                        } else {
                            showSnackbar(res.message || `Notification sent to ${res.data?.recipientCount || effectiveSelectedCount} customers!`);
                        }
                        setComposeForm({ title: '', body: '', imageUrl: '', navigateTo: '', navigateParams: null, navigateLabel: '' });
                        setSelectedIds([]);
                        setSelectAll(false);
                    } else {
                        showSnackbar(res.message || 'Failed to send notification', 'error');
                    }
                } catch (error) {
                    showSnackbar('Error sending notification', 'error');
                } finally {
                    setSending(false);
                }
            },
        });
    };

    // Search debounce
    const [searchTimeout, setSearchTimeout] = useState(null);
    const handleSearchChange = (value) => {
        setFilters(prev => ({ ...prev, searchTerm: value }));
        if (searchTimeout) clearTimeout(searchTimeout);
        setSearchTimeout(setTimeout(() => fetchCustomers(1), 500));
    };

    return (
        <div className="p-4 md:p-6">
            {/* Header — simplified (no back button), aligned with /branches style */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <FaPaperPlane className="text-blue-500" /> Send Notifications
                    </h1>
                    <p className="text-sm text-gray-500 mt-0.5">Send push notifications to selected customers</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-gray-200">
                {[
                    { key: 'compose', label: 'Compose & Send', icon: <FaPaperPlane /> },
                    { key: 'history', label: 'Campaign History', icon: <FaHistory /> },
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === tab.key
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        {tab.icon} {tab.label}
                    </button>
                ))}
            </div>

            {/* ===================================== */}
            {/* COMPOSE TAB */}
            {/* ===================================== */}
            {activeTab === 'compose' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* LEFT: Customer Selection */}
                    <div className="lg:col-span-2 space-y-4">
                        {/* Search & Filter Controls */}
                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="flex-1 relative">
                                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={filters.searchTerm}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        placeholder="Search by name, email, or mobile..."
                                    />
                                </div>
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                        showFilters ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    <FaFilter /> Filters {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                                </button>
                            </div>

                            {/* Expanded Filters */}
                            <AnimatePresence>
                                {showFilters && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="pt-3 border-t border-gray-200 space-y-3">
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Min Orders</label>
                                                    <input type="number" min="0" value={filters.minOrders} onChange={(e) => setFilters(prev => ({ ...prev, minOrders: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Max Orders</label>
                                                    <input type="number" min="0" value={filters.maxOrders} onChange={(e) => setFilters(prev => ({ ...prev, maxOrders: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="100" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Min Revenue (₹)</label>
                                                    <input type="number" min="0" value={filters.minRevenue} onChange={(e) => setFilters(prev => ({ ...prev, minRevenue: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Max Revenue (₹)</label>
                                                    <input type="number" min="0" value={filters.maxRevenue} onChange={(e) => setFilters(prev => ({ ...prev, maxRevenue: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="50000" />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Last Order From</label>
                                                    <input type="date" value={filters.lastOrderFrom} onChange={(e) => setFilters(prev => ({ ...prev, lastOrderFrom: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Last Order To</label>
                                                    <input type="date" value={filters.lastOrderTo} onChange={(e) => setFilters(prev => ({ ...prev, lastOrderTo: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Gender</label>
                                                    <select value={filters.gender} onChange={(e) => setFilters(prev => ({ ...prev, gender: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                                                        <option value="">All</option>
                                                        <option value="Male">Male</option>
                                                        <option value="Female">Female</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Has Orders</label>
                                                    <select value={filters.hasOrders} onChange={(e) => setFilters(prev => ({ ...prev, hasOrders: e.target.value }))}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                                                        <option value="">All</option>
                                                        <option value="true">With Orders</option>
                                                        <option value="false">No Orders</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button onClick={handleApplyFilters}
                                                    className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600">
                                                    Apply Filters
                                                </button>
                                                <button onClick={handleClearFilters}
                                                    className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">
                                                    Clear
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Customer Table */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            {/* Table Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                                <div className="flex items-center gap-3">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                            className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-gray-600">Select All ({pagination.total})</span>
                                    </label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <FaUsers className="text-blue-500" />
                                    <span className="text-sm font-medium text-blue-600">
                                        {effectiveSelectedCount} selected
                                    </span>
                                </div>
                            </div>

                            {/* Table Body */}
                            {customersLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : customers.length === 0 ? (
                                <div className="text-center py-12">
                                    <FaUsers className="mx-auto text-2xl text-gray-300 mb-2" />
                                    <p className="text-gray-500 text-sm">No customers found</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                                    {customers.map(customer => {
                                        const cid = customer._id?.toString ? customer._id.toString() : customer._id;
                                        const isSelected = selectedIds.includes(cid);
                                        return (
                                            <div
                                                key={cid}
                                                onClick={() => toggleSelectCustomer(cid)}
                                                className={`flex items-center gap-4 px-4 py-3 cursor-pointer transition-colors ${
                                                    isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => {}}
                                                    className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-medium text-gray-800 truncate">{customer.name || 'Unknown'}</span>
                                                        {customer.gender && (
                                                            <span className="text-xs text-gray-400">{customer.gender}</span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-3 mt-0.5">
                                                        <span className="text-xs text-gray-400 truncate">{customer.mobile || customer.email || '-'}</span>
                                                    </div>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <div className="text-xs text-gray-500">{customer.orderCount || 0} orders</div>
                                                    <div className="text-xs text-green-600 font-medium">₹{(customer.totalRevenue || 0).toLocaleString('en-IN')}</div>
                                                    {customer.lastOrderDate && (
                                                        <div className="text-[10px] text-gray-400 mt-0.5">
                                                            Last: {(() => {
                                                                const d = new Date(customer.lastOrderDate);
                                                                if (isNaN(d.getTime())) return '-';
                                                                const day = String(d.getDate()).padStart(2, '0');
                                                                const month = d.toLocaleString('en-US', { month: 'short' });
                                                                const year = d.getFullYear();
                                                                const hours24 = d.getHours();
                                                                const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
                                                                const hh = String(hours12).padStart(2, '0');
                                                                const mm = String(d.getMinutes()).padStart(2, '0');
                                                                const ampm = hours24 >= 12 ? 'PM' : 'AM';
                                                                return `${day} ${month} ${year} ${hh}:${mm} ${ampm}`;
                                                            })()}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                                    <span className="text-xs text-gray-500">
                                        Page {pagination.page} of {pagination.totalPages} ({pagination.total} customers)
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => fetchCustomers(pagination.page - 1)}
                                            disabled={pagination.page <= 1}
                                            className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-100"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => fetchCustomers(pagination.page + 1)}
                                            disabled={pagination.page >= pagination.totalPages}
                                            className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-100"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT: Compose & Preview */}
                    <div className="space-y-4">
                        {/* Compose Form */}
                        <div className="bg-white rounded-xl border border-gray-200 p-5">
                            <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <FaBell className="text-blue-500" /> Compose Notification
                            </h3>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                                    <input
                                        type="text"
                                        value={composeForm.title}
                                        onChange={(e) => setComposeForm(prev => ({ ...prev, title: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                        placeholder="Notification title..."
                                        maxLength={100}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Body *</label>
                                    <textarea
                                        value={composeForm.body}
                                        onChange={(e) => setComposeForm(prev => ({ ...prev, body: e.target.value }))}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                        placeholder="Notification message..."
                                        maxLength={500}
                                    />
                                    <p className="text-xs text-gray-400 text-right mt-1">{composeForm.body.length}/500</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Image URL (Optional)</label>
                                    <input
                                        type="text"
                                        value={composeForm.imageUrl}
                                        onChange={(e) => setComposeForm(prev => ({ ...prev, imageUrl: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                        placeholder="https://..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Navigate To (On Tap) <span className="text-red-500">*</span>
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setNavigateModalOpen(true)}
                                        className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-lg text-sm transition-colors ${
                                            composeForm.navigateLabel
                                                ? 'border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100'
                                                : 'border-gray-300 bg-white text-gray-500 hover:border-blue-400 hover:bg-gray-50'
                                        }`}
                                    >
                                        <span className={`truncate pr-2 ${composeForm.navigateLabel ? 'font-medium' : ''}`}>
                                            {composeForm.navigateLabel || 'Choose destination (screen, product, banner, category...)'}
                                        </span>
                                        <FaChevronDown className="text-gray-400 shrink-0" />
                                    </button>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Required — where the customer lands when they tap this notification
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Preview */}
                        <div className="bg-white rounded-xl border border-gray-200 p-5">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                <FaMobileAlt className="text-blue-500" /> Preview
                            </h4>
                            <div className="bg-gray-900 rounded-xl p-4 text-white">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                                        <FaBell className="text-white text-xs" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-gray-400 mb-0.5">db ecart • now</p>
                                        <p className="text-sm font-semibold">
                                            {composeForm.title || 'Notification Title'}
                                        </p>
                                        <p className="text-xs text-gray-300 mt-1 break-words">
                                            {composeForm.body || 'Your notification message will appear here...'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Send Button */}
                        <button
                            onClick={handleSend}
                            disabled={sending || effectiveSelectedCount === 0 || !composeForm.title.trim() || !composeForm.body.trim() || !composeForm.navigateTo}
                            className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-blue-500 text-white rounded-xl text-sm font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25"
                        >
                            {sending ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <FaPaperPlane /> Send to {effectiveSelectedCount} Customer{effectiveSelectedCount !== 1 ? 's' : ''}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* ===================================== */}
            {/* HISTORY TAB */}
            {/* ===================================== */}
            {activeTab === 'history' && (
                <div className="space-y-3">
                    {campaignsLoading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    ) : campaigns.length === 0 ? (
                        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                            <FaHistory className="mx-auto text-4xl text-gray-300 mb-3" />
                            <h3 className="text-lg font-medium text-gray-600">No campaigns yet</h3>
                            <p className="text-sm text-gray-400 mt-1">Your notification campaigns will appear here</p>
                        </div>
                    ) : (
                        campaigns.map(campaign => (
                            <motion.div
                                key={campaign._id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-gray-800">{campaign.title}</h4>
                                        <p className="text-sm text-gray-500 mt-1">{campaign.body}</p>
                                        <div className="flex items-center gap-4 mt-3">
                                            {/* <span className="text-xs text-gray-400">
                                                {moment(campaign.createdAt).format('DD MMM YYYY, hh:mm A')}
                                            </span> */}
                                            <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-medium">
                                                {campaign.recipientCount} recipients
                                            </span>
                                            <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full font-medium">
                                                {campaign.pushSent || 0} delivered
                                            </span>
                                            {(campaign.pushFailed || 0) > 0 && (
                                                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">
                                                    {campaign.pushFailed} failed
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))
                    )}
                </div>
            )}

            {/* Snackbar */}
            <AnimatePresence>
                {snackbar.show && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
                        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 max-w-lg text-center ${
                            snackbar.type === 'error'
                                ? 'bg-red-500 text-white'
                                : snackbar.type === 'warning'
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-green-500 text-white'
                        }`}
                    >
                        {snackbar.type === 'error' || snackbar.type === 'warning'
                            ? <FaExclamationTriangle />
                            : <FaCheckCircle />}
                        {snackbar.message}
                    </motion.div>
                )}
            </AnimatePresence>

            <ConfirmModal
                visible={confirmModal.visible}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, visible: false }))}
                cancelButtonText="Cancel"
                confirmButtonText="Send"
            />

            <NotificationNavigateTargetModal
                visible={navigateModalOpen}
                onClose={() => setNavigateModalOpen(false)}
                currentSelection={
                    composeForm.navigateTo
                        ? { label: composeForm.navigateLabel || composeForm.navigateTo }
                        : null
                }
                onSelect={({ screen, params, label }) => {
                    setComposeForm((prev) => ({
                        ...prev,
                        navigateTo: screen,
                        navigateParams: params,
                        navigateLabel: label,
                    }));
                }}
            />
        </div>
    );
};

export default ManualNotifications;
